import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ApiError,
  api,
  downloadFile,
  type Report,
  type RunStatus,
  type Section3Row,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { InfoHint } from '../components/InfoHint';
import { EventsTable } from '../components/report/EventsTable';
import { RunProgressBanner } from '../components/RunProgressBanner';
import { usePersistedTableState } from '../hooks/usePersistedTableState';
import { useRunPolling } from '../hooks/useRunPolling';
import { STAGE_LABELS, getRunProgress } from '../hooks/useRunPolling';
import {
  section3SourceLabel,
  stageDisplayName,
  stageErrorForDisplay,
} from '../lib/reportFormat';

const STATUS_PILL: Record<RunStatus, string> = {
  CREATED: 'pill-info',
  RUNNING: 'pill-warn',
  SUCCESS: 'pill-success',
  FAILED: 'pill-danger',
};

const rubFmt = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  maximumFractionDigits: 0,
});

function formatMonth(month: string): string {
  return new Date(month).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
}

function formatRub(v: number | null | undefined): string {
  if (v == null) return '—';
  return rubFmt.format(v);
}

function formatNumber(v: number | null | undefined): string {
  if (v == null) return '—';
  return new Intl.NumberFormat('ru-RU').format(v);
}

function reportThreshold(report: Report, key: string): number | null {
  const thresholds = report.run.params?.thresholds;
  if (!thresholds || typeof thresholds !== 'object' || Array.isArray(thresholds)) return null;
  const value = Number((thresholds as Record<string, unknown>)[key]);
  return Number.isFinite(value) ? value : null;
}

function formatThresholdPercent(value: number | null): string {
  if (value == null) return 'заданного порога';
  return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(value * 100)}%`;
}

function section3TypeLabel(row: Section3Row): string {
  const type = row.entity_type?.toLowerCase();
  if (type === 'brand' || row.brand) return 'Бренд';
  if (type === 'seller' || row.seller) return 'Продавец';
  if (type === 'warning' || row.source === 'warning') return 'Предупреждение';
  return 'Сигнал';
}

function section3TypeClass(row: Section3Row): string {
  const type = row.entity_type?.toLowerCase();
  if (type === 'brand' || row.brand) return 'pill-success';
  if (type === 'seller' || row.seller) return 'pill-info';
  if (type === 'warning' || row.source === 'warning') return 'pill-warn';
  return 'pill-neutral';
}

function section3Name(row: Section3Row): string {
  return row.name ?? row.brand ?? row.seller ?? row.message ?? '—';
}

function compareNullableNumber(
  a: number | null | undefined,
  b: number | null | undefined,
  direction: 'asc' | 'desc',
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return direction === 'asc' ? a - b : b - a;
}

function compareText(
  a: string | null | undefined,
  b: string | null | undefined,
  direction: 'asc' | 'desc',
): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const result = a.localeCompare(b, 'ru');
  return direction === 'asc' ? result : -result;
}

type Section3SortBy = 'type' | 'name' | 'category' | 'revenue' | 'sales' | 'source';

type Section3TableState = {
  sortBy: Section3SortBy;
  sortDir: 'asc' | 'desc';
};

const SECTION3_TABLE_DEFAULTS: Section3TableState = {
  sortBy: 'revenue',
  sortDir: 'desc',
};
const SECTION3_TABLE_SORTING_KEYS: readonly (keyof Section3TableState)[] = [
  'sortBy',
  'sortDir',
];

type ReportPageProps = {
  /** Только разделы 1–7 без шапки (вкладка «Отчёт» в RunDetailPage). */
  sectionsOnly?: boolean;
  runId?: string;
};

export function ReportPage({ sectionsOnly = false, runId: runIdProp }: ReportPageProps = {}) {
  const { runId: runIdParam } = useParams<{ runId: string }>();
  const runId = runIdProp ?? runIdParam;
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    api
      .get<Report>(`/api/reports/${runId}`)
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  const polledRun = useRunPolling(runId, report?.run ?? null);

  if (error) {
    if (sectionsOnly) {
      return <div className="error-hint">{error}</div>;
    }
    return (
      <div className="flex flex-col gap-3">
        <Link to="/" className="btn-ghost btn-sm self-start">← К списку прогонов</Link>
        <div className="card card-padding">
          <div className="error-hint">{error}</div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="card card-padding text-text-tertiary">Загрузка отчёта…</div>
    );
  }

  const run = polledRun ?? report.run;
  const progress = getRunProgress(run);
  const stageLabel = progress.current_stage
    ? STAGE_LABELS[progress.current_stage] ?? progress.current_stage
    : null;

  if (sectionsOnly) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex justify-end">
          <ExportButtons runId={run.id} disabled={run.status !== 'SUCCESS'} />
        </div>
        <ReportSections report={report} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="section-header page-header">
        <div>
          <div className="rubric">Отчёт</div>
          <h1 className="text-h1 font-strong text-text-primary">
            {run.title}
          </h1>
          <div className="text-caption text-text-tertiary mt-1">
            {formatMonth(run.month)} · {run.marketplace}
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className={STATUS_PILL[run.status]} title={progress.current_detail ?? ''}>
              {run.status}
            </span>
            {stageLabel ? <span className="pill-neutral">{stageLabel}</span> : null}
            <span className="text-caption text-text-tertiary mono">id {run.id.slice(0, 8)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons runId={run.id} disabled={run.status !== 'SUCCESS'} />
          <Link to="/" className="btn-ghost btn-md">← К списку</Link>
        </div>
      </div>

      <RunProgressBanner run={run} />

      {run.error ? (
        <div className="card card-padding border-danger/30">
          <div className="rubric">Ошибка прогона</div>
          <div className="mono text-caption text-danger whitespace-pre-wrap break-all">
            {run.error}
          </div>
        </div>
      ) : null}

      {report.stages && report.stages.length > 0 ? (
        <section className="card card-padding">
          <div className="rubric mb-2">Ход выполнения</div>
          <ul className="space-y-1 text-caption">
            {report.stages.map((s) => (
              <li key={s.name} className="flex flex-wrap gap-2 items-center">
                <span className="text-text-secondary">{stageDisplayName(s.name)}</span>
                <span
                  className={
                    s.status === 'success'
                      ? 'pill-success'
                      : s.status === 'failed'
                        ? 'pill-danger'
                        : 'pill-neutral'
                  }
                >
                  {s.status}
                </span>
                {s.duration_sec != null ? (
                  <span className="text-text-tertiary">{s.duration_sec}s</span>
                ) : null}
                {s.error ? (
                  <span className="error-hint truncate max-w-full">
                    {stageErrorForDisplay(s.name, s.error)}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <ReportSections report={report} />
    </div>
  );
}

function Section3Table({ rows, runId }: { rows: Section3Row[]; runId: string }) {
  const { user } = useAuth();
  const [tableState, setTableState] = usePersistedTableState(
    user?.id,
    `report.${runId}.section3`,
    SECTION3_TABLE_DEFAULTS,
    {
      sortingKeys: SECTION3_TABLE_SORTING_KEYS,
      persistSorting: Boolean(user?.persist_table_sorting),
      persistFilters: Boolean(user?.persist_table_filters),
    },
  );
  const { sortBy, sortDir } = tableState;

  function updateSort(key: Section3SortBy) {
    if (sortBy === key) {
      setTableState((prev) => ({ ...prev, sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc' }));
    } else {
      setTableState((prev) => ({ ...prev, sortBy: key, sortDir: 'asc' }));
    }
  }

  function SortHeader({ label, value }: { label: string; value: Section3SortBy }) {
    const active = sortBy === value;
    return (
      <button
        type="button"
        className="text-caption text-text-secondary hover:text-text-primary"
        onClick={() => updateSort(value)}
      >
        {label}
        {active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
      </button>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="card card-padding text-text-tertiary">
        Нет данных для этого раздела.
      </div>
    );
  }

  const sorted = [...rows].sort((a, b) => {
    if (sortBy === 'type') return compareText(section3TypeLabel(a), section3TypeLabel(b), sortDir);
    if (sortBy === 'name') return compareText(section3Name(a), section3Name(b), sortDir);
    if (sortBy === 'category') return compareText(a.category_path, b.category_path, sortDir);
    if (sortBy === 'sales') return compareNullableNumber(a.sales, b.sales, sortDir);
    if (sortBy === 'source') {
      return compareText(section3SourceLabel(a.source), section3SourceLabel(b.source), sortDir);
    }
    return compareNullableNumber(a.revenue, b.revenue, sortDir);
  });

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th><SortHeader label="Тип" value="type" /></th>
            <th><SortHeader label="Название" value="name" /></th>
            <th><SortHeader label="Категория" value="category" /></th>
            <th className="text-right"><SortHeader label="Заказы, ₽" value="revenue" /></th>
            <th className="text-right"><SortHeader label="Заказы, шт." value="sales" /></th>
            <th><SortHeader label="Основание" value="source" /></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr key={i}>
              <td>
                <span className={section3TypeClass(row)}>{section3TypeLabel(row)}</span>
              </td>
              <td className="text-text-primary max-w-[360px] truncate" title={section3Name(row)}>
                {section3Name(row)}
              </td>
              <td className="mono text-caption max-w-[360px] truncate" title={row.category_path ?? ''}>
                {row.category_path ?? '—'}
              </td>
              <td className="text-right mono">{formatRub(row.revenue)}</td>
              <td className="text-right mono">{formatNumber(row.sales)}</td>
              <td className="text-caption">{section3SourceLabel(row.source)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportSections({ report }: { report: Report }) {
  const newWinnerMin = reportThreshold(report, 'new_winner_min_revenue');
  const growthThreshold = reportThreshold(report, 'growth_rate');
  const minPreviousRevenue = reportThreshold(report, 'min_prev_revenue');

  return (
    <>
      {/* Раздел 1 */}
      <section>
        <div className="section-header">
          <div>
            <div className="rubric">Раздел 1</div>
            <h2 className="inline-flex flex-wrap items-center gap-2 section-title">
              Новые карточки с заказами от {newWinnerMin == null ? 'заданного порога' : formatRub(newWinnerMin)}
              <InfoHint text="Карточки, впервые замеченные MPSTATS в анализируемом или двух предыдущих месяцах, с суммой заказов не ниже порога раздела 1." />
            </h2>
          </div>
          <span className="pill-success">{report.section_1_new_winners.length}</span>
        </div>
        <EventsTable
          events={report.section_1_new_winners}
          showGrowth={false}
          tableId={`report.${report.run.id}.section1`}
        />
      </section>

      {/* Раздел 2 */}
      <section>
        <div className="section-header">
          <div>
            <div className="rubric">Раздел 2</div>
            <h2 className="inline-flex flex-wrap items-center gap-2 section-title">
              Старые карточки с ростом более {formatThresholdPercent(growthThreshold)}
              <InfoHint text={`Карточки с историей минимум за два предыдущих месяца. Рост заказов должен быть выше порога раздела 2, а заказы прошлого месяца — не ниже ${minPreviousRevenue == null ? 'заданного минимума' : formatRub(minPreviousRevenue)}.`} />
            </h2>
          </div>
          <span className="pill-success">{report.section_2_hyper_growth.length}</span>
        </div>
        <EventsTable
          events={report.section_2_hyper_growth}
          showGrowth
          tableId={`report.${report.run.id}.section2`}
        />
      </section>

      {/* Раздел 3 */}
      <section>
        <div className="section-header">
          <div>
            <div className="rubric">Раздел 3</div>
            <h2 className="inline-flex flex-wrap items-center gap-2 section-title">
              Бренды / продавцы, новые относительно прошлого месяца
              <InfoHint text="Сущность есть в агрегате выбранного месяца и отсутствует в полученной выборке предыдущего. Пороги разделов 1–2 не применяются. Заказы приходят готовыми агрегатами MPSTATS; бренды и продавцы не склеиваются в пары." />
            </h2>
          </div>
          <span className="pill-neutral">{report.section_3_new_brands_sellers.length}</span>
        </div>
        <Section3Table rows={report.section_3_new_brands_sellers} runId={report.run.id} />
      </section>

      {/* Разделы 4–7 (AI) */}
      <section>
        <div className="section-header">
          <div>
            <div className="rubric">Раздел 4</div>
            <h2 className="inline-flex items-center gap-2 section-title">
              Общие признаки успешных новичков
              <InfoHint text="Сводка паттернов среди новых успешных карточек; LLM только комментирует уже найденные события." />
            </h2>
          </div>
        </div>
        <div className="card card-padding">
          {report.section_4_winner_patterns ? (
            <p className="text-body text-text-secondary whitespace-pre-wrap">
              {report.section_4_winner_patterns}
            </p>
          ) : (
            <div className="text-text-tertiary">
              AI-раздел ещё не сгенерирован. Появится после включения AI-сводок.
            </div>
          )}
        </div>
      </section>

      <section>
        <div className="section-header">
          <div>
            <div className="rubric">Раздел 5</div>
            <h2 className="inline-flex items-center gap-2 section-title">
              Общие признаки карточек-гиперроста
              <InfoHint text="Сводка общих черт старых карточек с резким ростом." />
            </h2>
          </div>
        </div>
        <div className="card card-padding">
          {report.section_5_hyper_patterns ? (
            <p className="text-body text-text-secondary whitespace-pre-wrap">
              {report.section_5_hyper_patterns}
            </p>
          ) : (
            <div className="text-text-tertiary">AI-раздел ещё не сгенерирован.</div>
          )}
        </div>
      </section>

      <section>
        <div className="section-header">
          <div>
            <div className="rubric">Раздел 6</div>
            <h2 className="inline-flex items-center gap-2 section-title">
              Гипотезы причин
              <InfoHint text="Гипотезы формулируются на основе метрик и драйверов; это объяснение, а не поиск трендов." />
            </h2>
          </div>
        </div>
        <div className="card card-padding">
          {report.section_6_hypotheses ? (
            <p className="text-body text-text-secondary whitespace-pre-wrap">
              {report.section_6_hypotheses}
            </p>
          ) : (
            <div className="text-text-tertiary">
              Гипотезы по карточкам доступны в карточках SKU. Сводка по разделу ещё не сгенерирована.
            </div>
          )}
        </div>
      </section>

      <section>
        <div className="section-header">
          <div>
            <div className="rubric">Раздел 7</div>
            <h2 className="inline-flex items-center gap-2 section-title">
              Рекомендации
              <InfoHint text="Короткие следующие шаги на основе найденных событий и гипотез причин." />
            </h2>
          </div>
        </div>
        <div className="card card-padding">
          {report.section_7_recommendations ? (
            <p className="text-body text-text-secondary whitespace-pre-wrap">
              {report.section_7_recommendations}
            </p>
          ) : (
            <div className="text-text-tertiary">
              Рекомендации ещё не сгенерированы. Они появляются после завершения AI-обработки.
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function ExportButtons({ runId, disabled }: { runId: string; disabled: boolean }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handle(format: 'pdf' | 'csv' | 'xlsx') {
    setBusy(format);
    setError(null);
    try {
      await downloadFile(`/api/reports/${runId}/export?format=${format}`);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `Ошибка экспорта: ${err.message}`
          : String(err),
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex items-center gap-2">
      {(['pdf', 'csv', 'xlsx'] as const).map((fmt) => (
        <button
          key={fmt}
          type="button"
          className="btn-subtle btn-sm uppercase"
          onClick={() => handle(fmt)}
          disabled={disabled || busy !== null}
          title={
            disabled
              ? 'Экспорт доступен после успешного прогона'
              : `Скачать .${fmt}`
          }
        >
          {busy === fmt ? '…' : fmt}
        </button>
      ))}
      {error ? <span className="error-hint">{error}</span> : null}
    </div>
  );
}
